// Cross-device initialization for optimal compatibility
import { getDeviceInfo } from './deviceDetection';
import logger from './logger';
import { updateViewportHeightCSS } from './viewportHeight';

/**
 * Initialize cross-device support for mobile browsers
 */
export const initCrossDeviceSupport = () => {
  const device = getDeviceInfo();

  // Prevent double-tap zoom on iOS
  if (device.isIOS) {
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (event) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, { passive: false });
  }

  // Prevent pull-to-refresh on mobile
  if (device.isMobile || device.isTablet) {
    let startY = 0;
    document.addEventListener('touchstart', (e) => {
      startY = e.touches[0].pageY;
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      const y = e.touches[0].pageY;
      if (startY <= 10 && y > startY) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  // Handle orientation changes
  const handleOrientationChange = () => {
    // Force layout recalculation
    document.body.style.height = `${window.innerHeight}px`;
    setTimeout(() => {
      document.body.style.height = '';
    }, 100);
  };

  window.addEventListener('orientationchange', handleOrientationChange);
  window.screen?.orientation?.addEventListener('change', handleOrientationChange);

  // Optimize for low-end devices
  if (device.pixelRatio < 2 || (navigator.hardwareConcurrency || 2) < 4) {
    document.documentElement.classList.add('low-end-device');
  }

  // Add device-specific classes
  if (device.isIOS) document.documentElement.classList.add('ios');
  if (device.isAndroid) document.documentElement.classList.add('android');
  if (device.isMobile) document.documentElement.classList.add('mobile');
  if (device.isTablet) document.documentElement.classList.add('tablet');
  if (device.isIPad) document.documentElement.classList.add('ipad');
  if (device.isIPod) document.documentElement.classList.add('ipod');
  if (device.hasTouch) document.documentElement.classList.add('touch');
  if (device.isMac) document.documentElement.classList.add('mac');
  if (device.isWindows) document.documentElement.classList.add('windows');

  // Prevent context menu on long press for mobile
  if (device.isMobile || device.isTablet) {
    document.addEventListener('contextmenu', (e) => {
      if ((e.target as HTMLElement).tagName !== 'INPUT' && 
          (e.target as HTMLElement).tagName !== 'TEXTAREA') {
        e.preventDefault();
      }
    });
  }

  // Handle visibility changes (app backgrounding) - throttled to prevent spam
  let lastVisibilityLog = 0;
  document.addEventListener('visibilitychange', () => {
    const now = Date.now();
    if (now - lastVisibilityLog < 1000) return; // Throttle to 1 second
    lastVisibilityLog = now;
    
    if (document.hidden) {
    logger.log('App backgrounded');
    } else {
    logger.log('App foregrounded');
    }
  });

  // Optimize video playback for device
  const optimizeVideoPlayback = () => {
    const videos = document.querySelectorAll('video');
    videos.forEach((video) => {
      // iOS requires these attributes for inline playback
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('x-webkit-airplay', 'allow');
      
      // Prevent fullscreen on double-tap
      if (device.isMobile || device.isTablet) {
        video.style.pointerEvents = 'none';
      }
      
      // Optimize for low-end devices
      if (device.pixelRatio < 2 || (navigator.hardwareConcurrency || 2) < 4) {
        video.style.imageRendering = 'auto';
      }
    });
  };

  // Run optimization on DOM changes
  const observer = new MutationObserver(optimizeVideoPlayback);
  observer.observe(document.body, { childList: true, subtree: true });

  // Handle iOS standalone mode (PWA)
  if (device.isIOS && (window.navigator as any).standalone) {
    document.documentElement.classList.add('ios-standalone');
  }
  
  // Prevent zoom on double-tap for all devices
  let lastTouchTime = 0;
  document.addEventListener('touchstart', (e) => {
    const now = Date.now();
    if (now - lastTouchTime < 300) {
      e.preventDefault();
    }
    lastTouchTime = now;
  }, { passive: false });
  
  // Prevent pinch-to-zoom on all touch devices
  document.addEventListener('touchmove', (e) => {
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  }, { passive: false });
  
  // Prevent gesture events on iOS Safari
  document.addEventListener('gesturestart', (e) => e.preventDefault());
  document.addEventListener('gesturechange', (e) => e.preventDefault());
  document.addEventListener('gestureend', (e) => e.preventDefault());
  
  // Fix viewport height on mobile browsers
  updateViewportHeightCSS();
  
  logger.log('✅ Cross-device support initialized', {
    platform: device.isIOS ? 'iOS' : device.isAndroid ? 'Android' : device.isWindows ? 'Windows' : device.isMac ? 'macOS' : 'Unknown',
    deviceType: device.isMobile ? 'Mobile' : device.isTablet ? 'Tablet' : 'Desktop',
    browser: device.isChrome ? 'Chrome' : device.isSafari ? 'Safari' : device.isFirefox ? 'Firefox' : device.isEdge ? 'Edge' : 'Unknown',
    touchSupport: device.hasTouch,
    pixelRatio: device.pixelRatio
  });
};
